<script setup lang="ts">
import { computed } from 'vue';

import Checkbox from '@/components/Shared/Input/Checkbox/Checkbox.vue';
import UnlockedLockSvg from '@/assets/svg/unlocked-lock-20.svg?component';
import MeService from '@/core/shared/me/me.service';

// ONE-6727 / ONE-7144: a few accounts receive custom, individually-approved disclosure
// copy from the backend instead of the standard disclosure text below. The actual copy
// lives in ats-conversation (src/core/message/constants/disclosure.constant.ts) — that
// file is the source of truth and the text is intentionally NOT duplicated here, so the
// admin preview shows a generic note for those accounts instead of copy that could drift
// out of sync with what candidates actually see. Ryerson's copy is an SMS opt-out notice
// with no AI wording at all, so the note stays copy-agnostic.
const CUSTOM_DISCLOSURE_COPY_EMPLOYER_IDS = [
  5443, // Airtron Heating and Air Conditioning (ONE-6727)
  5374, // Ryerson (ONE-7144)
];

const meService = new MeService();

const hasCustomDisclosureCopy = computed(() => {
  const employerId = meService.employer?.id;
  return employerId !== undefined && CUSTOM_DISCLOSURE_COPY_EMPLOYER_IDS.includes(employerId);
});

defineProps<{
  modelValue: boolean;
  isVisible: boolean;
  isLocked?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();
</script>

<template>
  <div v-if="isVisible" class="w-full space-y-2" data-testid="candidate-ai-disclosure-field">
    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-2" data-testid="candidate-ai-disclosure-label-row">
        <Checkbox
          :class="{ 'candidate-ai-disclosure-field__checkbox--locked': isLocked }"
          :model-value="modelValue"
          :disabled="isLocked"
          data-testid="candidate-ai-disclosure-checkbox"
          @update:model-value="emit('update:modelValue', $event)"
        />

        <div class="flex items-center gap-2">
          <span
            :class="isLocked ? 'text-[#64748b]' : 'text-[#111928]'"
            class="text-sm font-bold leading-[21px]"
            data-testid="candidate-ai-disclosure-label"
          >
            Include candidate AI disclosure
          </span>

          <v-tooltip
            v-if="isLocked"
            location="top"
            content-class="project-card-tooltip tooltip-bottom-arrow"
          >
            <template #activator="{ props }">
              <div
                v-bind="props"
                class="inline-flex cursor-help items-center"
                data-testid="candidate-ai-disclosure-tooltip"
              >
                <UnlockedLockSvg class="text-[#111928]" />
              </div>
            </template>
            <span class="policy-tooltip-text">
              This option has been set to mandatory by your company's settings. Contact your admin
              for more information.
            </span>
          </v-tooltip>
        </div>
      </div>

      <p class="text-sm leading-[18px] text-[#64748b]" data-testid="candidate-ai-disclosure-helper">
        Shown once at the start of each new candidate conversation.
      </p>
    </div>

    <div
      v-if="modelValue"
      :class="isLocked ? 'border-[#e5f5fd] bg-[#f3fbff]' : 'border-[#d1eefa] bg-[#e5f5fd]'"
      class="w-full rounded-[8px] border p-3"
      data-testid="candidate-ai-disclosure-preview"
    >
      <p
        :class="isLocked ? 'text-[#64748b]' : 'text-black'"
        class="text-sm font-bold leading-[21px]"
        data-testid="candidate-ai-disclosure-preview-label"
      >
        Candidate will see:
      </p>
      <!-- ONE-6727 / ONE-7144: custom-copy accounts get a generic note because their
           candidates see custom backend-served copy; the standard preview would be
           inaccurate for them. -->
      <p
        v-if="hasCustomDisclosureCopy"
        :class="isLocked ? 'text-[#64748b]' : 'text-black'"
        class="text-sm italic leading-[21px]"
        data-test="candidate-ai-disclosure-preview-custom-note"
      >
        Your account uses custom disclosure copy approved with FactoryFix.
      </p>
      <p
        v-else
        :class="isLocked ? 'text-[#64748b]' : 'text-black'"
        class="text-sm italic leading-[21px]"
        data-testid="candidate-ai-disclosure-preview-body"
      >
        We use AI tools to support recruiting, including some candidate communications. Human
        recruiters review candidates and make final hiring decisions.
      </p>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.candidate-ai-disclosure-field__checkbox--locked :deep(svg rect:first-of-type) {
  fill: #9f90f5;
  stroke: #9f90f5;
}
</style>
